import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Html5Qrcode } from 'html5-qrcode';
import { X, Camera, CheckCircle, XCircle, Loader2, Calendar } from './Icons';
import { bookingsAPI } from '../services/api';
import toast from 'react-hot-toast';

/**
 * QRScannerModal - In-app QR code scanner
 *
 * Handles two types of QR codes:
 * 1. Check-in: /check-in?s={scheduleId}&d={date} → performs check-in, navigates to rings
 * 2. Schedule: /{shortCode} or /s/{shortCode} (legacy) → navigates to schedule page
 */
export default function QRScannerModal({ isOpen, onClose, onCheckInSuccess }) {
  const navigate = useNavigate();
  const [status, setStatus] = useState('scanning'); // 'scanning' | 'processing' | 'success' | 'error' | 'schedule'
  const [message, setMessage] = useState('');
  const [className, setClassName] = useState('');
  const [scheduleCode, setScheduleCode] = useState('');
  const scannerRef = useRef(null);
  const html5QrCodeRef = useRef(null);

  useEffect(() => {
    if (isOpen && status === 'scanning') {
      startScanner();
    }

    return () => {
      stopScanner();
    };
  }, [isOpen]);

  const startScanner = async () => {
    try {
      const html5QrCode = new Html5Qrcode('qr-reader');
      html5QrCodeRef.current = html5QrCode;

      await html5QrCode.start(
        { facingMode: 'environment' },
        {
          fps: 10,
          qrbox: (viewfinderWidth, viewfinderHeight) => {
            const minDimension = Math.min(viewfinderWidth, viewfinderHeight);
            const qrboxSize = Math.floor(minDimension * 0.8);
            return { width: qrboxSize, height: qrboxSize };
          },
        },
        onScanSuccess,
        onScanFailure
      );

      // Setup pinch-to-zoom after scanner starts
      setTimeout(() => {
        setupPinchToZoom();
      }, 500);
    } catch (err) {
      console.error('Failed to start scanner:', err);
      setStatus('error');
      setMessage('Could not access camera. Please check permissions.');
    }
  };

  // Pinch-to-zoom functionality
  const setupPinchToZoom = () => {
    const videoElement = document.querySelector('#qr-reader video');
    if (!videoElement?.srcObject) return;

    const track = videoElement.srcObject.getVideoTracks()[0];
    const capabilities = track.getCapabilities?.();
    if (!capabilities?.zoom) return; // Device doesn't support zoom

    const minZoom = capabilities.zoom.min;
    const maxZoom = capabilities.zoom.max;
    let currentZoom = track.getSettings?.()?.zoom || minZoom;
    let initialDistance = 0;
    let initialZoom = currentZoom;

    const getDistance = (touches) => {
      const dx = touches[0].clientX - touches[1].clientX;
      const dy = touches[0].clientY - touches[1].clientY;
      return Math.sqrt(dx * dx + dy * dy);
    };

    const handleTouchStart = (e) => {
      if (e.touches.length === 2) {
        initialDistance = getDistance(e.touches);
        initialZoom = currentZoom;
      }
    };

    const handleTouchMove = async (e) => {
      if (e.touches.length === 2) {
        e.preventDefault();
        const currentDistance = getDistance(e.touches);
        const scale = currentDistance / initialDistance;
        const newZoom = Math.min(maxZoom, Math.max(minZoom, initialZoom * scale));

        try {
          await track.applyConstraints({ advanced: [{ zoom: newZoom }] });
          currentZoom = newZoom;
        } catch (err) {
          // Zoom failed, ignore
        }
      }
    };

    // Add listeners to the scanner container
    const container = document.querySelector('#qr-reader');
    if (container) {
      container.addEventListener('touchstart', handleTouchStart, { passive: true });
      container.addEventListener('touchmove', handleTouchMove, { passive: false });
    }
  };

  const stopScanner = async () => {
    if (html5QrCodeRef.current) {
      try {
        await html5QrCodeRef.current.stop();
        html5QrCodeRef.current = null;
      } catch (err) {
        // Scanner may already be stopped
      }
    }
  };

  const onScanSuccess = async (decodedText) => {
    // Stop scanning immediately
    await stopScanner();

    // Try to parse as check-in URL first
    const checkInParams = parseCheckInUrl(decodedText);
    if (checkInParams) {
      await performCheckIn(checkInParams.scheduleId, checkInParams.date);
      return;
    }

    // Try to parse as schedule URL
    const scheduleParams = parseScheduleUrl(decodedText);
    if (scheduleParams) {
      setScheduleCode(scheduleParams.shortCode);
      setStatus('schedule');
      setMessage('Schedule found! Redirecting...');
      toast.success('Schedule found!');

      // Navigate to schedule page after brief delay (new clean URL format)
      setTimeout(() => {
        onClose();
        navigate(`/${scheduleParams.shortCode}`);
      }, 1500);
      return;
    }

    // Neither format matched
    setStatus('error');
    setMessage('Invalid QR code. Please scan a valid check-in or schedule code.');
  };

  const onScanFailure = (error) => {
    // Ignore scan failures - these happen constantly while searching
  };

  const parseCheckInUrl = (url) => {
    try {
      // Handle both full URLs and relative paths
      let urlObj;
      if (url.startsWith('http')) {
        urlObj = new URL(url);
      } else if (url.includes('check-in')) {
        urlObj = new URL(url, 'https://placeholder.com');
      } else {
        return null;
      }

      // Check if it's a check-in URL
      if (!urlObj.pathname.includes('check-in')) {
        return null;
      }

      const scheduleId = urlObj.searchParams.get('s');
      const date = urlObj.searchParams.get('d');

      if (!scheduleId || !date) {
        return null;
      }

      return { scheduleId, date };
    } catch {
      return null;
    }
  };

  const parseScheduleUrl = (url) => {
    try {
      // Handle both full URLs and relative paths
      // Formats:
      //   New: /{shortCode} or https://mystudioclass.com/{shortCode}
      //   Legacy: /s/{shortCode} or https://mystudioclass.com/s/{shortCode}
      let pathname;

      if (url.startsWith('http')) {
        const urlObj = new URL(url);
        pathname = urlObj.pathname;
      } else if (url.startsWith('/')) {
        pathname = url;
      } else {
        return null;
      }

      // Check if it's a legacy schedule URL: /s/{shortCode}
      const legacyMatch = pathname.match(/^\/s\/([a-zA-Z0-9-]+)/);
      if (legacyMatch) {
        return { shortCode: legacyMatch[1] };
      }

      // Check if it's a new format schedule URL: /{shortCode}
      // Must not match reserved paths like /check-in, /login, /dashboard, etc.
      const reservedPaths = ['check-in', 'login', 'register', 'dashboard', 'schedule', 'classes', 'community', 'subscription', 'settings', 'rings', 'forgot-password', 'complete-profile', 'accept-invite'];
      const newMatch = pathname.match(/^\/([a-zA-Z0-9-]+)\/?$/);
      if (newMatch && !reservedPaths.includes(newMatch[1])) {
        return { shortCode: newMatch[1] };
      }

      return null;
    } catch {
      return null;
    }
  };

  const performCheckIn = async (scheduleId, date) => {
    setStatus('processing');
    setMessage('Checking you in...');

    try {
      const response = await bookingsAPI.checkIn({ scheduleId, date });

      if (response.success) {
        setStatus('success');
        setClassName(response.data?.className || 'your class');
        setMessage('You have successfully checked in!');
        toast.success('Checked in!');

        // Notify parent, close, and navigate to rings page after delay
        // Pass checkin=success so the ring page shows a satisfying animation
        setTimeout(() => {
          onCheckInSuccess?.();
          onClose();
          navigate('/rings?checkin=success');
        }, 2000);
      } else {
        setStatus('error');
        setMessage(response.message || 'Failed to check in');
      }
    } catch (error) {
      console.error('Check-in error:', error);
      setStatus('error');
      const errorData = error.response?.data;
      let errorMessage = errorData?.message || error.message || 'Failed to check in';
      // Show debug info if available
      if (errorData?.debug) {
        errorMessage += ` (ID: ${errorData.debug.scheduleId?.slice(0, 8)}...)`;
      }
      setMessage(errorMessage);
    }
  };

  const handleRetry = () => {
    setStatus('scanning');
    setMessage('');
    setScheduleCode('');
    startScanner();
  };

  const handleClose = () => {
    stopScanner();
    setStatus('scanning');
    setMessage('');
    setScheduleCode('');
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex flex-col" style={{ background: 'var(--bg-base)' }}>
      {/* Header - Always visible */}
      <div
        className="flex items-center justify-between p-4 safe-area-top"
        style={{
          background: 'var(--bg-surface)',
          borderBottom: '1px solid var(--border-subtle)'
        }}
      >
        <h2 className="text-lg font-semibold" style={{ color: 'var(--text-primary)' }}>
          Scan QR Code
        </h2>
        <button
          onClick={handleClose}
          className="w-10 h-10 flex items-center justify-center rounded-xl transition-colors"
          style={{ background: 'var(--highlight-10)' }}
        >
          <X className="w-5 h-5" style={{ color: 'var(--text-secondary)' }} />
        </button>
      </div>

      {/* Content - Full screen */}
      <div className="flex-1 flex flex-col items-center justify-center p-4 overflow-auto">
        {status === 'scanning' && (
          <div className="w-full max-w-lg flex flex-col items-center">
            {/* Scanner container - large and prominent */}
            <div
              id="qr-reader"
              ref={scannerRef}
              className="w-full aspect-square bg-black rounded-2xl overflow-hidden shadow-2xl"
              style={{ maxWidth: '400px' }}
            />
            {/* Scanning frame overlay hint */}
            <div className="mt-6 text-center">
              <p style={{ color: 'var(--text-secondary)', fontSize: '16px' }}>
                Point your camera at the QR code
              </p>
              <p style={{ color: 'var(--text-muted)', fontSize: '14px', marginTop: '8px' }}>
                Pinch to zoom • Works for check-in and schedule codes
              </p>
            </div>
          </div>
        )}

        {status === 'processing' && (
          <div className="text-center">
            <Loader2 className="w-20 h-20 mx-auto mb-6 animate-spin" style={{ color: 'var(--primary)' }} />
            <h3 className="text-xl font-semibold mb-2" style={{ color: 'var(--text-primary)' }}>
              Checking you in...
            </h3>
            <p style={{ color: 'var(--text-muted)' }}>Please wait a moment</p>
          </div>
        )}

        {status === 'success' && (
          <div className="text-center max-w-sm">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'var(--secondary-15)' }}
            >
              <CheckCircle className="w-14 h-14" style={{ color: 'var(--secondary)' }} />
            </div>
            <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--secondary)' }}>
              Checked In!
            </h3>
            <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>{message}</p>
            <p className="text-sm mb-6" style={{ color: 'var(--text-muted)' }}>
              Welcome to <span className="font-medium" style={{ color: 'var(--text-primary)' }}>{className}</span>
            </p>
            <div
              className="rounded-xl p-4"
              style={{ background: 'var(--secondary-10)', border: '1px solid var(--secondary-20)' }}
            >
              <p className="text-sm font-medium" style={{ color: 'var(--secondary)' }}>
                +1 added to your weekly ring!
              </p>
            </div>
          </div>
        )}

        {status === 'schedule' && (
          <div className="text-center max-w-sm">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'var(--primary-15)' }}
            >
              <Calendar className="w-14 h-14" style={{ color: 'var(--primary)' }} />
            </div>
            <h3 className="text-2xl font-bold mb-3" style={{ color: 'var(--primary)' }}>
              Schedule Found!
            </h3>
            <p className="mb-2" style={{ color: 'var(--text-secondary)' }}>
              Opening class schedule...
            </p>
            <div
              className="rounded-xl p-4 mt-6"
              style={{ background: 'var(--primary-10)', border: '1px solid var(--primary-20)' }}
            >
              <p className="text-sm font-medium" style={{ color: 'var(--primary)' }}>
                Redirecting to schedule
              </p>
            </div>
          </div>
        )}

        {status === 'error' && (
          <div className="text-center max-w-sm w-full">
            <div
              className="w-24 h-24 rounded-full flex items-center justify-center mx-auto mb-6"
              style={{ background: 'rgba(239, 68, 68, 0.1)' }}
            >
              <XCircle className="w-14 h-14" style={{ color: '#ef4444' }} />
            </div>
            <h3 className="text-2xl font-bold mb-3" style={{ color: '#ef4444' }}>
              Scan Failed
            </h3>
            <p className="mb-8" style={{ color: 'var(--text-secondary)' }}>{message}</p>
            <div className="flex gap-3">
              <button
                onClick={handleClose}
                className="flex-1 px-4 py-3 rounded-xl font-medium transition-colors"
                style={{
                  background: 'var(--highlight-10)',
                  color: 'var(--text-secondary)',
                  border: '1px solid var(--border-subtle)'
                }}
              >
                Close
              </button>
              <button
                onClick={handleRetry}
                className="flex-1 px-4 py-3 rounded-xl font-medium transition-colors"
                style={{
                  background: 'linear-gradient(135deg, var(--primary-hover), var(--primary))',
                  color: 'var(--bg-base)'
                }}
              >
                Try Again
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
